import { useEffect, useState } from 'react';

/** Von Chromium ausgeloest, wenn die App die Installationskriterien erfuellt. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const isStandaloneMode = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  // iOS Safari kennt display-mode nicht und setzt stattdessen dieses Flag
  (window.navigator as unknown as { standalone?: boolean }).standalone === true;

const isIosDevice = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
  // iPadOS meldet sich seit 13 als Mac - der Touch-Support verraet es trotzdem
  (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);

/**
 * Stellt fest, ob die App installiert werden kann, und kapselt den Ablauf.
 *
 * Zwei Wege, weil die Browser sich unterscheiden:
 *  - Chromium liefert ein Event, das wir aufheben und spaeter ausloesen koennen.
 *  - iOS Safari hat keine API dafuer; dort bleibt nur eine Anleitung ueber das Teilen-Menue.
 */
export const useInstallPrompt = () => {
  const [deferredEvent, setDeferredEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [standalone, setStandalone] = useState(isStandaloneMode);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      // Verhindert das automatische Mini-Banner, damit wir selbst bestimmen, wann gefragt wird
      event.preventDefault();
      setDeferredEvent(event as BeforeInstallPromptEvent);
    };
    const handleAppInstalled = () => {
      setDeferredEvent(null);
      setInstalled(true);
    };
    const displayModeQuery = window.matchMedia('(display-mode: standalone)');
    const handleDisplayModeChange = () => setStandalone(isStandaloneMode());

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    displayModeQuery.addEventListener('change', handleDisplayModeChange);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      displayModeQuery.removeEventListener('change', handleDisplayModeChange);
    };
  }, []);

  const alreadyInstalled = standalone || installed;
  const canPrompt = !!deferredEvent && !alreadyInstalled;
  const needsIosHint = isIosDevice() && !alreadyInstalled;

  const promptInstall = async () => {
    if (!deferredEvent) return;
    await deferredEvent.prompt();
    await deferredEvent.userChoice;
    // Das Event ist danach verbraucht und darf nicht erneut ausgeloest werden
    setDeferredEvent(null);
  };

  return {
    /** Soll ein Installations-Eintrag angeboten werden? */
    isAvailable: canPrompt || needsIosHint,
    /** true = direkter Dialog moeglich, false = nur Anleitung (iOS) */
    canPrompt,
    promptInstall
  };
};
